import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useIsCompactLayout } from '@/common/hooks';
import { MODULE_ALIASES, getModuleMeta } from '@/types/modules';
import './ModuleStyles.css';
import { NewsConfigModal } from '@/modules/news/ConfigModal';
import { ConfigModal as ShopifyConfigModal } from '@/modules/shopify-crm/ConfigModal';
import { ConfigModal as MixpanelConfigModal } from '@/modules/mixpanel/ConfigModal';
import { ModuleMenu, ModuleEditModal } from './ModuleMenu';
import { IntegrationAccountsModal } from '@/modules/_infrastructure/IntegrationAccountsModal';
import { ManageAccountsModal } from '@/modules/email/ManageAccountsModal';
import { isV2 } from '@/modules/email/provider-display';
import { useSession } from '@/features/auth/hooks/useSession';
import { resolveWorkspaceId } from '@/lib/workspace-routing';
import { api } from '@/lib/api';
import { sanitizeMixpanelModuleConfig } from '@/modules/mixpanel/utils/sanitizeConfig';
import { queryKeys } from '@/lib/query/keys';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/apiClient';
import { emailApiPath } from '@/lib/email-api'
import { ABOUT_ICONS } from './constants';
import { MODULE_COMPONENTS } from './components/ModuleComponents';
import { isDriveHiddenModuleType } from '@/lib/drive-deployment';
import { useCalendar24Hour } from '@/modules/calendar/lib/timeFormat';
import type { ModuleRendererProps } from './types';

export default function ModuleRenderer(props: ModuleRendererProps) {
  const { module, filterText, conversationId: explicitConversationId, onArchived, onExpand } = props;
  const isMobile = useIsCompactLayout();
  const { token, selectedContainer } = useSession();
  const workspaceId = resolveWorkspaceId(selectedContainer);
  const queryClient = useQueryClient();
  const [showIntegrationAccountsModal, setShowIntegrationAccountsModal] = useState(false);
  const [showManageAccountsModal, setShowManageAccountsModal] = useState(false);
  const [showAboutDialog, setShowAboutDialog] = useState(false);
  const [showNewsConfigModal, setShowNewsConfigModal] = useState(false);
  const [showShopifyConfigModal, setShowShopifyConfigModal] = useState(false);
  const [showMixpanelConfigModal, setShowMixpanelConfigModal] = useState(false);
  // Scope data to the active conversation rendered by View/Chat; fallback only when absent.
  const conversationId = explicitConversationId ?? module.conversation_id ?? '';
  const canonicalType = MODULE_ALIASES[module.type] ?? module.type;
  const meta = getModuleMeta(module.type);
  // User-set label always wins; fall back to catalog default, then type string.
  const displayLabel = module.label || meta?.defaultLabel || module.type;
  const isReport = canonicalType === 'report';
  // Mixpanel is registered by plugin id ('mixpanel' / 'app-analytics'), neither of
  // which is part of CanonicalModuleType — compare as a plain string to stay typed.
  const isMixpanelModule = (canonicalType as string) === 'mixpanel' || (canonicalType as string) === 'app-analytics';
  const Component = MODULE_COMPONENTS[canonicalType];
  // Calendar 24-hour clock preference — global (localStorage-backed), so the
  // toggle is offered on every calendar surface, not just the chat-dock pane.
  const [use24Hour, setUse24Hour] = useCalendar24Hour();

  // Drive-only deployment: never render a disabled module type, even if reached
  // by a direct route / deep link (nav + picker affordances are hidden
  // elsewhere). Stable per mounted instance, like the !Component guard below.
  if (isDriveHiddenModuleType(canonicalType) || isDriveHiddenModuleType(module.type)) {
    return null;
  }

  if (!Component) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-100">
        Unknown module type: <code>{module.type}</code>
      </div>
    );
  }

  // Email status (for account-gated menu items). Only queries when this is a email module.
  const { data: emailStatus } = useQuery<{ connected?: boolean; accounts?: Array<{ id: string; enabledForModule?: boolean }> }>({
    queryKey: ['email-status-module', module.id],
    queryFn: () => apiFetch(emailApiPath() + '/status', {
      searchParams: { moduleId: module.id },
      authToken: token ?? undefined,
    }),
    enabled: canonicalType === 'email' && Boolean(token),
    staleTime: 30_000,
  });
  const hasEmailAccount = (emailStatus?.accounts?.length ?? 0) > 0;

  // Track Email sync-in-progress for this specific module so the menu can show
  // a spinner on the Sync item, keep itself open while sync runs, and close
  // automatically once sync completes.
  const [isEmailSyncing, setIsEmailSyncing] = useState(false);
  const [menuCloseSignal, setMenuCloseSignal] = useState(0);
  const [showRenameModal, setShowRenameModal] = useState(false);
  // Inline header rename (#3598): double-click the title to edit in place.
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  // Guards the input's onBlur from double-saving (Enter commits + blurs) or
  // persisting a cancelled edit (Escape blurs the field).
  const skipTitleBlurRef = useRef(false);
  useEffect(() => {
    if (canonicalType !== 'email') return;
    const onStart = (e: Event) => {
      if ((e as CustomEvent).detail?.moduleId === module.id) setIsEmailSyncing(true);
    };
    const onEnd = (e: Event) => {
      if ((e as CustomEvent).detail?.moduleId === module.id) {
        setIsEmailSyncing(false);
        setMenuCloseSignal(s => s + 1);
      }
    };
    window.addEventListener('xavior:email-sync-started', onStart);
    window.addEventListener('xavior:email-sync-completed', onEnd);
    return () => {
      window.removeEventListener('xavior:email-sync-started', onStart);
      window.removeEventListener('xavior:email-sync-completed', onEnd);
    };
  }, [canonicalType, module.id]);

  // Build menu items — About is always present
  const menuItems: Array<{ label: string; icon?: React.ReactNode; onClick: (anchorRect?: DOMRect) => void; keepOpenOnClick?: boolean; disabled?: boolean; toggle?: boolean }> = [];
  menuItems.push({
    label: 'Rename',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
    ),
    onClick: () => setShowRenameModal(true),
  });
  if (canonicalType === 'email') {
    menuItems.push({
      label: 'Email Tab Settings',
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      ),
      onClick: () => window.dispatchEvent(new CustomEvent('xavior:email-open-settings', { detail: { moduleId: module.id } })),
    });
    menuItems.push({
      label: 'Manage Accounts',
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
      onClick: () => setShowManageAccountsModal(true),
    });
  }
  if (canonicalType === 'event_list' || canonicalType === 'calendar') {
    menuItems.push({
      label: 'Manage Accounts',
      icon: isV2
        ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
        : <img src="/google-logo.svg" width="14" height="14" alt="Google" />,
      onClick: () => isV2 ? setShowManageAccountsModal(true) : setShowIntegrationAccountsModal(true),
    });
  }
  if ((canonicalType === 'event_list' || canonicalType === 'calendar') && isMobile) {
    menuItems.push({
      label: 'Sync Calendar',
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
      ),
      onClick: () => {
        window.dispatchEvent(new CustomEvent('xavior:sync-calendar', { detail: { moduleId: module.id } }));
      },
    });
  }
  if (canonicalType === 'event_list' || canonicalType === 'calendar') {
    // 24-hour clock toggle — renders a blue slider; keepOpenOnClick lets the
    // user see it flip. Mirrors the chat-dock pane menu (ChatModulePane) so the
    // toggle exists on the standalone calendar surface too.
    menuItems.push({
      label: '24h Clock',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>,
      onClick: () => setUse24Hour(!use24Hour),
      keepOpenOnClick: true,
      toggle: use24Hour,
    });
  }
  if (isReport) {
    menuItems.push({
      label: 'Change Theme',
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
          <path d="M2 12h20" />
        </svg>
      ),
      onClick: () => {
        window.dispatchEvent(new CustomEvent('xavior:open-theme-picker', {
          detail: { moduleId: module.id },
        }));
      },
    });
  }
  if (canonicalType === 'contact_list') {
    menuItems.push({
      label: 'Manage Accounts',
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
      onClick: () => window.dispatchEvent(new CustomEvent('xavior:contacts-manage-accounts', { detail: { moduleId: module.id } })),
    });
    menuItems.push({
      label: 'Enhance with AI',
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2l2.39 5.26L20 9l-4 4 1 6-5-3-5 3 1-6-4-4 5.61-1.74L12 2z" />
        </svg>
      ),
      onClick: () => {
        window.dispatchEvent(new CustomEvent('xavior:contacts-bulk-enhance', { detail: { moduleId: module.id } }));
      },
    });
    menuItems.push({
      label: 'Email Extract',
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
        </svg>
      ),
      onClick: () => {
        window.dispatchEvent(new CustomEvent('xavior:contacts-discover-open', { detail: { moduleId: module.id } }));
      },
    });
    menuItems.push({
      label: 'Find duplicates',
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-4.35-4.35" />
        </svg>
      ),
      onClick: () => {
        window.dispatchEvent(new CustomEvent('xavior:contacts-duplicates-open', { detail: { moduleId: module.id } }));
      },
    });
    menuItems.push({
      label: 'Column header names',
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="5" height="18" rx="1" />
          <rect x="10" y="3" width="5" height="18" rx="1" />
          <rect x="17" y="3" width="5" height="18" rx="1" />
        </svg>
      ),
      onClick: () => {
        window.dispatchEvent(new CustomEvent('xavior:contacts-template-open', { detail: { moduleId: module.id } }));
      },
    });
  }
  // View Archived intentionally appended further down (after meta?.about)
  // so module-specific actions surface above this generic toggle.
  if (canonicalType === 'news') {
    menuItems.push({
      label: 'Settings',
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      ),
      onClick: () => setShowNewsConfigModal(true),
    });
  }
  if (canonicalType === 'shopify-crm') {
    menuItems.push({
      label: 'Settings',
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      ),
      onClick: () => setShowShopifyConfigModal(true),
    });
  }
  if (isMixpanelModule) {
    menuItems.push({
      label: 'Settings',
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      ),
      onClick: () => setShowMixpanelConfigModal(true),
    });
  }
  if (canonicalType === 'health') {
    menuItems.push({
      label: 'Manage Wearables',
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="5" y="2" width="14" height="20" rx="7" ry="7" />
          <line x1="12" y1="18" x2="12" y2="18.01" />
        </svg>
      ),
      onClick: () => {
        window.dispatchEvent(new CustomEvent('xavior:health-manage-wearables', { detail: { moduleId: module.id } }));
      },
    });
    menuItems.push({
      label: 'Sync Wearable',
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
      ),
      onClick: () => {
        window.dispatchEvent(new CustomEvent('xavior:health-sync-wearable', { detail: { moduleId: module.id } }));
      },
    });
    menuItems.push({
      label: 'Set Objectives',
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
        </svg>
      ),
      onClick: () => {
        window.dispatchEvent(new CustomEvent('xavior:health-set-objectives', { detail: { moduleId: module.id } }));
      },
    });
  }
  if (meta?.about) {
    menuItems.push({
      label: 'About',
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      ),
      onClick: () => setShowAboutDialog(true),
    });
  }
  // View Archived — surfaced first for contacts, pinned to the end elsewhere.
  if (canonicalType === 'task_list' || canonicalType === 'kanban' || canonicalType === 'contact_list' || canonicalType === 'pages' || canonicalType === 'place_list' || canonicalType === 'places' || canonicalType === 'drive' || canonicalType === 'notes') {
    const viewArchivedItem = {
      label: 'View Archived',
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" />
        </svg>
      ),
      onClick: () => {
        const archivedEvent = canonicalType === 'drive'
          ? 'xavior:toggle-archived-drive'
          : canonicalType === 'notes'
            ? 'xavior:toggle-archived-notes'
            : 'xavior:toggle-archived-tasks';
        window.dispatchEvent(new CustomEvent(archivedEvent, { detail: { moduleId: module.id } }));
      },
    };
    if (canonicalType === 'contact_list') menuItems.unshift(viewArchivedItem);
    else menuItems.push(viewArchivedItem);
  }

  // Listen for About dialog event from ChatBox module pane
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.moduleId === module.id && meta?.about) {
        setShowAboutDialog(true);
      }
    };
    window.addEventListener('xavior:show-module-about', handler);
    return () => window.removeEventListener('xavior:show-module-about', handler);
  }, [module.id, meta?.about]);

  // Listen for the in-panel "Connect Mixpanel" / Reconfigure buttons (AppAnalyticsPanel
  // dispatches this via onOpenConfig) so they open the same ConfigModal as the menu item.
  useEffect(() => {
    if (!isMixpanelModule) return;
    const handler = (e: Event) => {
      if ((e as CustomEvent).detail?.moduleId === module.id) setShowMixpanelConfigModal(true);
    };
    window.addEventListener('xavior:open-mixpanel-config', handler);
    return () => window.removeEventListener('xavior:open-mixpanel-config', handler);
  }, [isMixpanelModule, module.id]);

  // Inline header rename (#3598). Persists the label workspace-scoped and
  // invalidates the modules query both the header and sidebar read, so a rename
  // here reflects in the sidebar immediately (and vice-versa).
  const startTitleEdit = () => {
    skipTitleBlurRef.current = false;
    setTitleDraft(displayLabel);
    setIsEditingTitle(true);
  };

  const cancelTitleEdit = () => {
    skipTitleBlurRef.current = true;
    setIsEditingTitle(false);
  };

  const handleModuleTitleSave = async (nextLabel: string) => {
    const trimmed = nextLabel.trim();
    setIsEditingTitle(false);
    if (!trimmed || trimmed === displayLabel) return;
    if (!token || !workspaceId) return;
    try {
      await api.workspaces.updateWorkspaceModule(token, workspaceId, module.id, { label: trimmed });
      queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });
    } catch (err) {
      console.error('[ModuleRenderer] Failed to rename module:', err);
    }
  };

  const commitTitleFromBlur = (value: string) => {
    // Enter already committed (and blurs the field) and Escape cancelled — in
    // both cases the trailing blur must be a no-op.
    if (skipTitleBlurRef.current) {
      skipTitleBlurRef.current = false;
      return;
    }
    void handleModuleTitleSave(value);
  };

  return (
    <div className={`module-renderer-wrapper${isReport ? ' is-report-module' : ''}`}>
      <div className="module-renderer-header">
        {isEditingTitle ? (
          <input
            className="module-renderer-title module-renderer-title-input"
            defaultValue={titleDraft}
            autoFocus
            onFocus={(e) => e.currentTarget.select()}
            onBlur={(e) => commitTitleFromBlur(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                skipTitleBlurRef.current = true;
                void handleModuleTitleSave(e.currentTarget.value);
                e.currentTarget.blur();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                cancelTitleEdit();
                e.currentTarget.blur();
              }
            }}
          />
        ) : (
          <span
            className="module-renderer-title"
            onDoubleClick={startTitleEdit}
            title="Double-click to rename"
          >
            {displayLabel}
          </span>
        )}
        <div className="module-renderer-actions">
          {onExpand && (
            <button
              className="module-toolbar-button is-icon-only"
              onClick={onExpand}
              title="Full screen"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M8 3H3V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M16 3H21V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M8 21H3V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M16 21H21V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
          {menuItems.length > 0 && (
          <ModuleMenu
            moduleId={module.id}
            moduleType={canonicalType}
            moduleConfig={module.config}
            conversationId={conversationId}
            onArchived={onArchived}
            customItems={menuItems}
            closeSignal={menuCloseSignal}
          />
          )}
        </div>
      </div>
      <Component module={module} conversationId={conversationId} filterText={filterText} />
      {showRenameModal && (
        <ModuleEditModal
          currentLabel={module.label || ''}
          currentColor={(module.config as any)?.highlightColor ?? null}
          currentIcon={(module.config as any)?.icon ?? null}
          onConfirm={async (result) => {
            setShowRenameModal(false);
            if (!token || !workspaceId) return;
            try {
              const updatedConfig = { ...(module.config || {}), highlightColor: result.highlightColor, icon: result.icon };
              await api.workspaces.updateWorkspaceModule(token, workspaceId, module.id, { label: result.label, config: updatedConfig });
              queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });
            } catch (err) {
              console.error('[ModuleRenderer] Failed to rename module:', err);
            }
          }}
          onCancel={() => setShowRenameModal(false)}
        />
      )}
      {showIntegrationAccountsModal && (
        <IntegrationAccountsModal onClose={() => setShowIntegrationAccountsModal(false)} moduleId={module.id} />
      )}
      {showManageAccountsModal && (
        <ManageAccountsModal onClose={() => setShowManageAccountsModal(false)} moduleId={module.id} />
      )}
      {showNewsConfigModal && (
        <NewsConfigModal
          isOpen={showNewsConfigModal}
          onClose={() => setShowNewsConfigModal(false)}
          config={(module.config as Record<string, string>) ?? {}}
          onSave={async (newConfig) => {
            if (!token || !workspaceId) return;
            try {
              await api.workspaces.updateWorkspaceModule(token, workspaceId, module.id, { config: newConfig as Record<string, unknown> });
              queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });
            } catch (err) {
              console.error('[ModuleRenderer] Failed to save news config:', err);
            }
          }}
        />
      )}
      {showShopifyConfigModal && (
        <ShopifyConfigModal
          isOpen={showShopifyConfigModal}
          onClose={() => setShowShopifyConfigModal(false)}
          config={(module.config as Record<string, string>) ?? {}}
          onSave={async (newConfig) => {
            if (!token || !workspaceId) return;
            try {
              await api.workspaces.updateWorkspaceModule(token, workspaceId, module.id, { config: newConfig as Record<string, unknown> });
              queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });
            } catch (err) {
              console.error('[ModuleRenderer] Failed to save Shopify config:', err);
            }
          }}
          instanceId={module.id}
        />
      )}
      {showMixpanelConfigModal && (
        <MixpanelConfigModal
          isOpen={showMixpanelConfigModal}
          onClose={() => setShowMixpanelConfigModal(false)}
          config={(module.config as unknown as Record<string, string>) ?? {}}
          onSave={async (newConfig) => {
            if (!token || !workspaceId) return;
            // The Mixpanel module config is read back verbatim by the client
            // (AppAnalyticsPanel reads module.config) and returned unsanitized by
            // GET /modules, so the raw service-account secrets must never be
            // persisted here. Mirror the server-side sanitizePluginConfig: strip
            // serviceAccountUser/serviceAccountSecret/removeServiceAccount and keep
            // only a hasServiceAccount flag. (Advanced-query secrets live in the
            // plugin_instances store, not module.config, so dropping them here does
            // not regress that flow.)
            const safeConfig = sanitizeMixpanelModuleConfig(
              module.config as Record<string, unknown> | undefined,
              newConfig as Record<string, unknown>,
            );
            await api.workspaces.updateWorkspaceModule(token, workspaceId, module.id, { config: safeConfig });
            queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });
          }}
          instanceId={module.id}
        />
      )}
      {showAboutDialog && meta?.about && createPortal(
        <div
          className="module-about-overlay"
          onClick={() => setShowAboutDialog(false)}
        >
          <div className="module-about-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="module-about-icon" style={{ background: `${meta.aboutColor || '#6366f1'}14` }}>
              {ABOUT_ICONS[canonicalType]?.(meta.aboutColor || '#6366f1') || <span style={{ fontSize: 40 }}>{meta.icon}</span>}
            </div>
            <h3 className="module-about-title">{displayLabel}</h3>
            <p className="module-about-text">{meta.about}</p>
            <button
              className="module-about-close"
              onClick={() => setShowAboutDialog(false)}
            >
              Got it
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
